import React from 'react'
import { useParams } from 'react-router'

import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { epochToDate } from '@sputnik/components/utils'
import AddPersonDrawer, {
  type AddPersonDrawerOpenable,
} from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/AddPersonDrawer'
import { useUpdatePeopleInfoData } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/UpdatePeopleInfoData.gql'
import { AddPersonForm } from '@sputnik/schema/creditApplication/addPerson'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

const AddCompanyOwnerDrawer = ({ isShowing, onClose }: AddPersonDrawerOpenable) => {
  const { appId } = useParams()
  const showToast = useToast()
  const { track } = useWebTracking()

  const [updateCompanyOwner] = useUpdatePeopleInfoData({
    onError: (error) => {
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Unable to add company owner'
          id='sputnik.AddCompanyOwnerDrawer__v2gL1j'
        />,
        {
          className: 'fs-unmask',
          autoHideDelay: 5000,
        },
      )
      logError(error, {
        attributes: {
          action: 'updateCompanyOwner',
          creditAppId: appId,
          result: error,
          message: 'Error while adding company owner.',
        },
        eventName: 'updateCompanyOwner',
      })
    },
  })

  const onSubmit = async (formValues: AddPersonForm) => {
    track('se_signup__add_person_drawer_saved__fired', {
      person_type: 'companyOwner',
    })
    await updateCompanyOwner({
      variables: {
        beneficialOwners: [
          {
            dob: epochToDate(formValues.dob) || '',
            email: formValues.email || '',
            firstName: formValues.firstName || '',
            lastName: formValues.lastName || '',
            phoneNumber: formValues.phoneNumber || '',
            ssn: formValues.ssn?.value || '',
            passportCountry: formValues.passportCountry || '',
            passportNumber: formValues.passportNumber?.value || '',
            passportExpiration: formValues.passportExpiration || '',
            address: {
              street: formValues.street || '',
              streetAdditional: formValues.streetAdditional || '',
              city: formValues.city || '',
              state: formValues.state || '',
              postalCode: formValues.postalCode || '',
              countryCode: formValues.countryCode || 'US',
            },
          },
        ],
        creditApplicationId: appId ?? '',
      },
      onCompleted: () => {
        logInfo('updateCompanyOwner', {
          attributes: {
            creditAppId: appId,
            result: `company owner added successfully`,
          },
          eventName: 'updateCompanyOwner',
        })
        showToast(
          TOAST_TYPE_SUCCESS,
          <FormattedMessage
            defaultMessage='Company owner added'
            id='sputnik.AddCompanyOwnerDrawer__O4W+07'
          />,
          {
            autoHideDelay: 5000,
          },
        )
        onClose()
      },
    })
  }

  return (
    isShowing && (
      <AddPersonDrawer
        isShowing={isShowing}
        title={
          <FormattedMessage
            defaultMessage='Add company owner'
            id='sputnik.AddCompanyOwnerDrawer__CllEVw'
          />
        }
        onClose={onClose}
        onSubmit={onSubmit}
      />
    )
  )
}

export default AddCompanyOwnerDrawer
